import { isTicketOverdue } from "../utils/ticketDueDate";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ticketsApi, companiesApi, techniciansApi, projectsApi, maintenanceApi, usersApi, contactsApi, agentsApi, agentRunsApi, alertsApi } from "../services/api";
import TicketCreateForm, { EMPTY_TICKET_FORM } from "../components/TicketCreateForm";
import { useNotification } from "../context/NotificationContext";
import { useFilterMine } from "../context/FilterMineContext";
import { useAuth } from "../context/AuthContext";
import { attachmentCount, ticketHasAttachments } from "../utils/attachments";
import { confirmTicketDeletion } from "../utils/ticketDeletion";
import { assigneeUserId } from "../utils/assignee";
import { ticketStatusLabel, ticketStatusSelectOptions } from "../components/ticketStatusVocabulary";

// ── Tickets stat cards ────────────────────────────────────────────────────────

const _OPEN_STATUSES_DASH = ["open","in_progress","pending","waiting"];
const _AI_REVIEW_STATUSES = new Set(["awaiting_technician_review", "awaiting_approval"]);
const STAT_CARDS = [
  { key: "active",      label: 'סה"כ פתוחות', filter: (t) => _OPEN_STATUSES_DASH.includes(t.status), noFilter: true, color: "border-indigo-300 bg-indigo-50 text-indigo-700",  ring: "ring-indigo-500" },
  { key: "in_progress", label: "בטיפול",      filter: (t) => t.status === "in_progress",                                                            color: "border-yellow-200 bg-yellow-50 text-yellow-700", ring: "ring-yellow-400" },
  { key: "pending",     label: "המתנה",       filter: (t) => t.status === "pending",                                                                 color: "border-purple-200 bg-purple-50 text-purple-700", ring: "ring-purple-400" },
  { key: "waiting",     label: "ממתין לתגובה", filter: (t) => t.status === "waiting",                                                               color: "border-cyan-200 bg-cyan-50 text-cyan-700",       ring: "ring-cyan-400" },
  { key: "unassigned",  label: "לא משובצות",  filter: (t) => _OPEN_STATUSES_DASH.includes(t.status) && t.technician_id === null,                    color: "border-orange-200 bg-orange-50 text-orange-700", ring: "ring-orange-400" },
  { key: "sla_breach",  label: "⚠ SLA",       filter: (t) => t.is_sla_breach,                                                                        color: "border-red-300 bg-red-50 text-red-700",          ring: "ring-red-500"    },
  { key: "ai_review",   label: "🤖 AI לסקירה", filter: (t) => _AI_REVIEW_STATUSES.has(t.pending_ai_run_status),                                    color: "border-violet-200 bg-violet-50 text-violet-700", ring: "ring-violet-400" },
];

// ── Queue tabs ────────────────────────────────────────────────────────────────

const QUEUE_TABS = [
  { id: "tickets",     label: "📋 כרטיסים",  color: "#2563eb" },
  { id: "projects",    label: "📁 פרויקטים", color: "#7c3aed" },
  { id: "maintenance", label: "🔧 תחזוקה",   color: "#059669" },
];

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_BADGE = {
  open:        "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  pending:     "bg-purple-100 text-purple-700",
  closed:      "bg-gray-100 text-gray-500",
  merged:      "bg-teal-100 text-teal-700",
};
const QUEUE_STATUS_BADGE = {
  unassigned: "bg-gray-100 text-gray-600",
  open:       "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  waiting:    "bg-purple-100 text-purple-700",
  closed:     "bg-gray-100 text-gray-400",
};
const QUEUE_STATUS_HE = { unassigned: "לא הוקצה", open: "פתוח", in_progress: "בטיפול", waiting: "המתנה", closed: "סגור" };

const CATEGORY_BADGE = {
  backups:        { label: "גיבויים",      cls: "bg-blue-100 text-blue-700 border border-blue-200" },
  m365:           { label: "M365",          cls: "bg-purple-100 text-purple-700 border border-purple-200" },
  av_edr:         { label: "AV/EDR",        cls: "bg-red-100 text-red-700 border border-red-200" },
  updates:        { label: "עדכונים",       cls: "bg-orange-100 text-orange-700 border border-orange-200" },
  firewall:       { label: "חומת אש",       cls: "bg-green-100 text-green-700 border border-green-200" },
  email_security: { label: "אבטחת מייל",   cls: "bg-cyan-100 text-cyan-700 border border-cyan-200" },
  reports:        { label: "דוחות",         cls: "bg-indigo-100 text-indigo-700 border border-indigo-200" },
};
const AI_CATEGORY_HE = { Network: "רשת", Hardware: "חומרה", Software: "תוכנה", Email: "דואר", Security: "אבטחה", Other: "אחר" };

// ── Formatting helpers ────────────────────────────────────────────────────────

const asUtc = (iso) => /Z|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z";
const DASH_PAGE_SIZE_OPTIONS = [25, 50, 100];
const TICKET_PRIORITY_OPTIONS = [
  { value: "high", label: "גבוהה" },
  { value: "medium", label: "בינונית" },
  { value: "low", label: "נמוכה" },
];
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(asUtc(iso));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("tickets");

  // ── Company filter (shared across all tabs) ────────────────────────────────
  const [companyFilter, setCompanyFilter] = useState("");
  const [technicianFilter, setTechnicianFilter] = useState("");

  // ── Page size (shared across all tabs, persisted) ──────────────────────────
  const [dashPageSize, setDashPageSize] = useState(
    () => {
      const saved = Number(localStorage.getItem("dashboard_page_size") || 50);
      return DASH_PAGE_SIZE_OPTIONS.includes(saved) ? saved : 50;
    }
  );
  const handleDashPageSize = (v) => {
    setDashPageSize(v);
    localStorage.setItem("dashboard_page_size", String(v));
  };

  // ── Create ticket form ─────────────────────────────────────────────────────
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketForm, setTicketForm]             = useState(EMPTY_TICKET_FORM);

  // ── Create project modal ───────────────────────────────────────────────────
  const [showCreateProject, setShowCreateProject]   = useState(false);
  const [projectForm, setProjectForm]               = useState({ title: "", company_id: "", assigned_to: "", priority: "medium", status: "unassigned" });

  // ── Create maintenance modal ───────────────────────────────────────────────
  const [showCreateMaint, setShowCreateMaint]       = useState(false);
  const [maintForm, setMaintForm]                   = useState({ title: "", category: "backups", company_id: "", assigned_to: "", priority: "medium", due_date: "", recurring: false, recurrence_pattern: "" });

  // ── Tickets state ──────────────────────────────────────────────────────────
  const [activeFilter, setActiveFilter]           = useState(() => {
    const f = searchParams.get("filter");
    return STAT_CARDS.some((c) => c.key === f && !c.noFilter) ? f : null;
  });

  const [sortField,     setSortField]             = useState("opened_at");
  const [sortDirection, setSortDirection]         = useState("desc");
  const { addNotification }                       = useNotification();
  const { showOnlyMine }                          = useFilterMine();
  const { user }                                  = useAuth();
  const [selectedTickets, setSelectedTickets]     = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkCloseConfirm, setShowBulkCloseConfirm] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkAssignTechnicianId, setBulkAssignTechnicianId] = useState("");
  const [showMergeDialog, setShowMergeDialog]     = useState(false);
  const [showSplitDialog, setShowSplitDialog]     = useState(false);
  const [splitForm, setSplitForm]                 = useState({ new_subject: "", note: "", technician_id: "" });
  // project/maintenance bulk state
  const [selectedProjectItems, setSelectedProjectItems] = useState(new Set()); // "project:id" | "ticket:id"
  const [selectedMaintItems, setSelectedMaintItems]     = useState(new Set()); // "maintenance:id" | "ticket:id"
  const [showBulkDeleteProjects, setShowBulkDeleteProjects] = useState(false);
  const [showBulkDeleteMaint, setShowBulkDeleteMaint]       = useState(false);
  const [showMergeProjects, setShowMergeProjects]           = useState(false);
  const [showMergeMaint, setShowMergeMaint]                 = useState(false);
  const [mergePrimaryProject, setMergePrimaryProject]       = useState(null);
  const [mergePrimaryMaint, setMergePrimaryMaint]           = useState(null);

  const prevOpenIdsRef  = useRef(null);
  const prevReplyIdsRef = useRef(null);

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: _rawTickets = [], isLoading, isError: ticketsFailed } = useQuery({
    queryKey: ["tickets-dashboard"],
    queryFn: () => ticketsApi.list({ limit: 2000, open_only: true }),
    staleTime: 0,
    refetchInterval: 30_000,
  });
  const { data: companies   = [] } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });
  const { data: technicians = [] } = useQuery({
    queryKey: ["technicians"],
    queryFn: () => techniciansApi.list(),
  });
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: () => contactsApi.list(),
  });

  const { data: projectStats, isLoading: isLoadingProjectStats, isError: projectStatsFailed } = useQuery({
    queryKey: ["projects-dashboard-stats"],
    queryFn: () => projectsApi.dashboardStats(),
  });
  const { data: _rawProjects = [], isLoading: isLoadingProjects } = useQuery({
    queryKey: ["projects-dashboard"],
    // This tab intentionally lists closed projects too (status badge/filter), so it
    // cannot use open_only; request the backend's max instead of the previous 200 so
    // an older active project isn't pushed out of the newest-first window by newer
    // closed ones (same fix class as KnowledgePage's 100->500 in 6bea4eb).
    queryFn: () => projectsApi.list({ limit: 500 }),
    enabled: activeTab === "projects" || Boolean(companyFilter || technicianFilter),
  });
  const { data: _rawProjectTickets = [], isLoading: isLoadingProjectTickets } = useQuery({
    queryKey: ["tickets-project-queue"],
    queryFn: () => ticketsApi.list({ queue_type: "project", limit: 500 }),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => usersApi.list(),
    staleTime: 300_000,
  });

  const { data: maintenanceStats, isLoading: isLoadingMaintenanceStats, isError: maintenanceStatsFailed } = useQuery({
    queryKey: ["maintenance-dashboard-stats"],
    queryFn: () => maintenanceApi.dashboardStats(),
  });
  const { data: _rawMaintenanceItems = [], isLoading: isLoadingMaintenance } = useQuery({
    queryKey: ["maintenance-dashboard"],
    queryFn: () => maintenanceApi.list({ limit: 500 }),
    enabled: activeTab === "maintenance" || Boolean(companyFilter || technicianFilter),
  });
  const { data: _rawMaintenanceTickets = [], isLoading: isLoadingMaintenanceTickets } = useQuery({
    queryKey: ["tickets-maintenance-queue"],
    queryFn: () => ticketsApi.list({ queue_type: "maintenance", limit: 500 }),
  });

  // ── AI Operations ─────────────────────────────────────────────────────────
  const { data: _agents = [] } = useQuery({
    queryKey: ["agents-dash"],
    queryFn: () => agentsApi.list(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const { data: _recentRuns = [] } = useQuery({
    queryKey: ["agent-runs-recent"],
    queryFn: () => agentRunsApi.listAll({ limit: 5 }),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  const aiPendingCount = _agents.reduce((sum, a) => sum + (a.pending_runs_count || 0), 0);
  const _agentNameMap = Object.fromEntries(_agents.map((a) => [a.id, a.name]));
  const { data: _alertsEval, isError: alertsFailed } = useQuery({
    queryKey: ["alerts-eval-dash"],
    queryFn: alertsApi.evaluate,
    staleTime: 120_000,
    refetchInterval: 120_000,
  });
  const _activeAlertCount = _alertsEval?.alerts?.length || 0;
  const { data: _crisisMode, isError: _crisisModeFailed } = useQuery({
    queryKey: ["crisis-mode"],
    queryFn: agentsApi.getCrisisMode,
    refetchInterval: 30000,
  });
  // A failed load must not silently read as "not in crisis": ?? false would hide a genuinely
  // active crisis banner from every technician exactly when the AI-action-pause warning matters
  // most. The 30s refetchInterval self-heals on the next successful poll either way.
  const _crisisActive = !_crisisModeFailed && (_crisisMode?.active ?? false);

  // ── "Show only mine" + company filter ────────────────────────────────────
  const myTechId = useMemo(
    () => technicians.find((t) => t.email?.toLowerCase() === user?.email?.toLowerCase())?.id ?? null,
    [technicians, user?.email]
  );
  const selectedTechnicianUserId = useMemo(() => {
    if (!technicianFilter) return null;
    const technician = technicians.find((t) => t.id === technicianFilter);
    if (!technician) return null;
    return technician.user_id ?? users.find((u) => u.email?.toLowerCase() === technician.email?.toLowerCase())?.id ?? null;
  }, [technicianFilter, technicians, users]);

  const matchesTechnicianFilter = useCallback((item) => {
    if (!technicianFilter) return true;
    if (item.technician_id === technicianFilter) return true;
    if (item.assignee?.technician_id === technicianFilter) return true;
    return selectedTechnicianUserId ? assigneeUserId(item) === selectedTechnicianUserId : false;
  }, [technicianFilter, selectedTechnicianUserId]);

  const tickets = useMemo(() => {
    let r = showOnlyMine ? _rawTickets.filter((t) => myTechId ? t.technician_id === myTechId : false) : _rawTickets;
    if (companyFilter) r = r.filter((t) => t.company_id === companyFilter);
    if (technicianFilter) r = r.filter(matchesTechnicianFilter);
    return r;
  }, [_rawTickets, showOnlyMine, myTechId, companyFilter, technicianFilter, matchesTechnicianFilter]);
  const projects = useMemo(() => {
    let r = showOnlyMine && user?.id ? _rawProjects.filter((p) => assigneeUserId(p) === user.id) : _rawProjects;
    if (companyFilter) r = r.filter((p) => p.company?.id === companyFilter);
    if (technicianFilter) r = r.filter(matchesTechnicianFilter);
    return r;
  }, [_rawProjects, showOnlyMine, user?.id, companyFilter, technicianFilter, matchesTechnicianFilter]);
  const projectTickets = useMemo(() => {
    let r = showOnlyMine ? _rawProjectTickets.filter((t) => myTechId ? t.technician_id === myTechId : false) : _rawProjectTickets;
    if (companyFilter) r = r.filter((t) => t.company_id === companyFilter);
    if (technicianFilter) r = r.filter(matchesTechnicianFilter);
    return r;
  }, [_rawProjectTickets, showOnlyMine, myTechId, companyFilter, technicianFilter, matchesTechnicianFilter]);
  const maintenanceItems = useMemo(() => {
    let r = showOnlyMine && user?.id ? _rawMaintenanceItems.filter((m) => assigneeUserId(m) === user.id) : _rawMaintenanceItems;
    if (companyFilter) r = r.filter((m) => m.company?.id === companyFilter);
    if (technicianFilter) r = r.filter(matchesTechnicianFilter);
    return r;
  }, [_rawMaintenanceItems, showOnlyMine, user?.id, companyFilter, technicianFilter, matchesTechnicianFilter]);
  const maintenanceTickets = useMemo(() => {
    let r = showOnlyMine ? _rawMaintenanceTickets.filter((t) => myTechId ? t.technician_id === myTechId : false) : _rawMaintenanceTickets;
    if (companyFilter) r = r.filter((t) => t.company_id === companyFilter);
    if (technicianFilter) r = r.filter(matchesTechnicianFilter);
    return r;
  }, [_rawMaintenanceTickets, showOnlyMine, myTechId, companyFilter, technicianFilter, matchesTechnicianFilter]);

  // ── Computed KPI stats from filtered data ──────────────────────────────────
  const computedProjectStats = useMemo(() => {
    const CLOSED = new Set(["closed", "merged"]);
    const activeP = projects.filter((p) => p.status !== "closed");
    const activeT = projectTickets.filter((t) => !CLOSED.has(t.status));
    return {
      total_active: activeP.length + activeT.length,
      // Defer to the canonical Ticket.is_sla_breach flag already on each ticket (same field the
      // ⚠ SLA badge and the sla_breach stat card use) instead of re-hardcoding "open + unassigned +
      // > 6h" here: that literal reimplementation missed the UNASSIGNED status entirely (is_sla_breach
      // treats OPEN and UNASSIGNED as equivalent "not yet picked up" states -- see models/ticket.py),
      // so an unassigned, SLA-breaching project ticket was silently excluded from "באיחור".
      overdue: activeT.filter((t) => t.is_sla_breach).length,
      unassigned: activeP.filter((p) => !assigneeUserId(p)).length + activeT.filter((t) => !t.technician_id).length,
      waiting: activeP.filter((p) => p.status === "waiting").length + activeT.filter((t) => ["pending", "waiting"].includes(t.status)).length,
      in_progress: activeP.filter((p) => ["open", "in_progress"].includes(p.status)).length + activeT.filter((t) => ["open", "in_progress"].includes(t.status)).length,
    };
  }, [projects, projectTickets]);

  const computedMaintStats = useMemo(() => {
    const DONE = new Set(["done", "cancelled", "closed"]);
    const CLOSED = new Set(["closed", "merged"]);
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const activeM = maintenanceItems.filter((m) => !DONE.has(m.status));
    const activeT = maintenanceTickets.filter((t) => !CLOSED.has(t.status));
    return {
      total_active: activeM.length + activeT.length,
      urgent: activeM.filter((m) => ["high", "critical"].includes(m.priority)).length + activeT.filter((t) => t.is_sla_breach).length,
      unassigned: activeM.filter((m) => !assigneeUserId(m)).length + activeT.filter((t) => !t.technician_id).length,
      waiting: activeM.filter((m) => m.status === "waiting").length + activeT.filter((t) => ["pending", "waiting"].includes(t.status)).length,
      completed_this_month:
        maintenanceItems.filter((m) => ["done", "closed"].includes(m.status) && m.updated_at && new Date(asUtc(m.updated_at)) >= firstOfMonth).length +
        maintenanceTickets.filter((t) => t.status === "closed" && t.updated_at && new Date(asUtc(t.updated_at)) >= firstOfMonth).length,
    };
  }, [maintenanceItems, maintenanceTickets]);
  // The unfiltered Maintenance KPI cards must prefer the backend's own /maintenance/dashboard-stats
  // values (same convention already used for the tab-count badge above) rather than computedMaintStats:
  // the backend's completed_this_month falls back to closed_at when a closed ticket later got a
  // follow-up comment/edit that bumped updated_at (see maintenance.py), and its urgent count also
  // includes high-priority (not just SLA-breached) maintenance-queue tickets -- computedMaintStats
  // matches neither, since it exists only to serve the FILTERED (company/technician) view the backend
  // endpoint does not support.
  const maintStatsUnfiltered = !(companyFilter || technicianFilter);
  const maintStat = (key) => (maintStatsUnfiltered ? maintenanceStats?.[key] : computedMaintStats[key]);
  const maintStatsLoading = isLoadingMaintenance || isLoadingMaintenanceTickets || (maintStatsUnfiltered && isLoadingMaintenanceStats);

  // ── Notification polling (tickets only) ───────────────────────────────────
  // "New" is decided on the UNFILTERED list: the effect used to diff the filtered one, so flipping "only mine" or a company
  // filter announced every already-open ticket that became visible as "new ticket / new customer reply", and a persisted
  // "only mine" (technicians loading after the tickets) did the same on every dashboard load. Only tickets that are new on the
  // server AND visible under the current filters are announced.
  useEffect(() => {
    if (isLoading) return;

    const OPEN = new Set(["open", "in_progress", "pending", "waiting"]);
    const openTickets  = _rawTickets.filter((t) => OPEN.has(t.status));
    const replyTickets = _rawTickets.filter((t) => t.has_customer_reply && OPEN.has(t.status));
    const visibleIds = new Set(tickets.map((t) => t.id));

    const openIds  = new Set(openTickets.map((t) => t.id));
    const replyIds = new Set(replyTickets.map((t) => t.id));

    if (prevOpenIdsRef.current !== null) {
      const newTickets = openTickets.filter((t) => !prevOpenIdsRef.current.has(t.id) && visibleIds.has(t.id));
      for (const ticket of newTickets) {
        addNotification("קריאה חדשה נפתחה", "ticket", `/tickets/${ticket.id}`, ticket.sender_name || ticket.sender_email);
      }
    }
    if (prevReplyIdsRef.current !== null) {
      const newReplyTickets = replyTickets.filter((t) => !prevReplyIdsRef.current.has(t.id) && visibleIds.has(t.id));
      for (const ticket of newReplyTickets) {
        addNotification("תגובה חדשה מלקוח", "reply", `/tickets/${ticket.id}`, ticket.sender_name || ticket.sender_email);
      }
    }

    prevOpenIdsRef.current  = openIds;
    prevReplyIdsRef.current = replyIds;
  }, [_rawTickets, tickets, isLoading, addNotification]);

  // ── Ticket mutations ───────────────────────────────────────────────────────
  const inlineUpdate = useMutation({
    mutationFn: ({ id, data }) => ticketsApi.update(id, data),
    onSuccess: (updatedTicket) => {
      const patch = (key) =>
        qc.setQueryData(key, (old) =>
          old?.map((t) => (t.id === updatedTicket.id ? { ...t, ...updatedTicket } : t))
        );
      patch(["tickets-dashboard"]);
      patch(["tickets"]);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון הקריאה", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => ticketsApi.delete(id),
    onSuccess: (_, id) => {
      const remove = (key) =>
        qc.setQueryData(key, (old) => old?.filter((t) => t.id !== id));
      remove(["tickets-dashboard"]);
      remove(["tickets"]);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת הקריאה", "error"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => ticketsApi.bulkDelete(ids),
    onSuccess: (_, ids) => {
      const idSet = new Set(ids);
      const remove = (key) =>
        qc.setQueryData(key, (old) => old?.filter((t) => !idSet.has(t.id)));
      remove(["tickets-dashboard"]);
      remove(["tickets"]);
      setSelectedTickets(new Set());
      setShowBulkDeleteConfirm(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת הקריאות המסומנות", "error"),
  });

  const bulkCloseMutation = useMutation({
    mutationFn: (ids) => ticketsApi.bulkUpdateStatus(ids, "closed"),
    onSuccess: async () => {
      setSelectedTickets(new Set());
      setShowBulkCloseConfirm(false);
      await qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      await qc.invalidateQueries({ queryKey: ["tickets"] });
      addNotification("הקריאות המסומנות נסגרו בהצלחה");
    },
    onError: (error) => {
      addNotification(error?.response?.data?.detail || "שגיאה בסגירת הקריאות המסומנות");
    },
  });

  const bulkAssignMutation = useMutation({
    mutationFn: ({ ids, technicianId }) => ticketsApi.bulkAssign(ids, technicianId),
    onSuccess: async () => {
      setSelectedTickets(new Set());
      setShowBulkAssignDialog(false);
      setBulkAssignTechnicianId("");
      await qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      await qc.invalidateQueries({ queryKey: ["tickets"] });
      addNotification("הקריאות המסומנות הוקצו בהצלחה");
    },
    onError: (error) => {
      addNotification(error?.response?.data?.detail || "שגיאה בהקצאת הקריאות המסומנות");
    },
  });

  const mergeMutation = useMutation({
    mutationFn: ({ primaryId, secondaryIds }) => ticketsApi.merge(primaryId, secondaryIds),
    onSuccess: (updatedPrimary, { secondaryIds }) => {
      const secSet = new Set(secondaryIds);
      const patch = (key) =>
        qc.setQueryData(key, (old) => old?.map((t) =>
          t.id === updatedPrimary.id ? { ...t, ...updatedPrimary } :
          secSet.has(t.id)           ? { ...t, status: "merged" }   : t
        ));
      patch(["tickets-dashboard"]);
      patch(["tickets"]);
      setSelectedTickets(new Set());
      setShowMergeDialog(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באיחוד הקריאות", "error"),
  });

  const splitMutation = useMutation({
    mutationFn: (data) => ticketsApi.split(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      setSelectedTickets(new Set());
      setShowSplitDialog(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בפיצול הקריאה", "error"),
  });

  const createTicketMutation = useMutation({
    mutationFn: ticketsApi.create,
    onSuccess: (createdTicket) => {
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      setShowCreateTicket(false);
      setTicketForm(EMPTY_TICKET_FORM);
      navigate(`/tickets/${createdTicket.id}`);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת הקריאה", "error"),
  });

  const handleTicketSubmit = (e) => {
    e.preventDefault();
    createTicketMutation.mutate({
      ...ticketForm,
      technician_id: ticketForm.technician_id || null,
      due_date: ticketForm.due_date || null,
      company_id: ticketForm.company_id || null,
      contact_id: ticketForm.contact_id || null,
    });
  };

  // ── Sort helpers (tickets) ─────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField !== field)          { setSortField(field); setSortDirection("asc"); }
    else if (sortDirection === "asc")   setSortDirection("desc");
    else                              { setSortField("opened_at"); setSortDirection("desc"); }
  };

  const applySort = (list) => {
    if (!sortField) return list;
    return [...list].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case "sender":
          valA = a.sender_name || ""; valB = b.sender_name || ""; break;
        case "company":
          valA = a.company?.name || ""; valB = b.company?.name || ""; break;
        case "technician":
          valA = a.technician ? `${a.technician.first_name} ${a.technician.last_name}` : "ת";
          valB = b.technician ? `${b.technician.first_name} ${b.technician.last_name}` : "ת";
          break;
        case "status":
          valA = a.status || ""; valB = b.status || ""; break;
        case "opened_at":
          return sortDirection === "asc"
            ? new Date(a.opened_at) - new Date(b.opened_at)
            : new Date(b.opened_at) - new Date(a.opened_at);
        case "updated_at":
          return sortDirection === "asc"
            ? new Date(a.updated_at) - new Date(b.updated_at)
            : new Date(b.updated_at) - new Date(a.updated_at);
        default:
          return 0;
      }
      return sortDirection === "asc"
        ? valA.localeCompare(valB, "he")
        : valB.localeCompare(valA, "he");
    });
  };

  const si = (f) => sortField === f ? (sortDirection === "asc" ? " ▲" : " ▼") : "";

  const handleCardClick = (key) => setActiveFilter((prev) => (prev === key ? null : key));

  // Keep the URL in sync with the selected stat-card filter so it survives a page
  // refresh and so links into a ticket can carry the exact list to return to.
  useEffect(() => {
    setSearchParams(activeFilter ? { filter: activeFilter } : {}, { replace: true });
  }, [activeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCard  = STAT_CARDS.find((c) => c.key === activeFilter);
  const openTickets = tickets.filter((t) => _OPEN_STATUSES_DASH.includes(t.status));
  const displayed   = activeCard ? tickets.filter(activeCard.filter) : openTickets;
  const sorted      = applySort(displayed);
  // Same ordered id list as the visible rows below, carried into ticket detail so
  // Previous/Next can walk this exact filtered+sorted set without a Dashboard round-trip.
  const visibleTicketIds = sorted.slice(0, dashPageSize).map((t) => t.id);

  // ── Create mutations ───────────────────────────────────────────────────────
  const createProjectMutation = useMutation({
    mutationFn: (data) => projectsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects-dashboard"] });
      qc.invalidateQueries({ queryKey: ["projects-dashboard-stats"] });
      setShowCreateProject(false);
      setProjectForm({ title: "", company_id: "", assigned_to: "", priority: "medium", status: "unassigned" });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת הפרויקט", "error"),
  });

  const createMaintMutation = useMutation({
    mutationFn: (data) => maintenanceApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance-dashboard"] });
      qc.invalidateQueries({ queryKey: ["maintenance-dashboard-stats"] });
      setShowCreateMaint(false);
      setMaintForm({ title: "", category: "backups", company_id: "", assigned_to: "", priority: "medium", due_date: "", recurring: false, recurrence_pattern: "" });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת המשימה", "error"),
  });

  // ── Project / maintenance bulk mutations ──────────────────────────────────
  const bulkDeleteProjectsMutation = useMutation({
    mutationFn: async ({ projectIds, ticketIds }) => {
      const tasks = [];
      if (projectIds.length) tasks.push(projectsApi.bulkDelete(projectIds));
      if (ticketIds.length)  tasks.push(ticketsApi.bulkDelete(ticketIds));
      await Promise.all(tasks);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects-dashboard"] });
      qc.invalidateQueries({ queryKey: ["projects-dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      setSelectedProjectItems(new Set());
      setShowBulkDeleteProjects(false);
    },
    onError: (err) => {
      // the project half may already have been deleted when the ticket half refuses (409): show the real state
      qc.invalidateQueries({ queryKey: ["projects-dashboard"] });
      qc.invalidateQueries({ queryKey: ["projects-dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      addNotification(err?.response?.data?.detail || "שגיאה במחיקת הפרויקטים", "error");
    },
  });

  const mergeProjectsMutation = useMutation({
    mutationFn: ({ type, primaryId, secondaryIds }) =>
      type === "project"
        ? projectsApi.merge(primaryId, secondaryIds)
        : ticketsApi.merge(primaryId, secondaryIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-project-queue"] });
      setSelectedProjectItems(new Set());
      setShowMergeProjects(false);
      setMergePrimaryProject(null);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באיחוד הפרויקטים", "error"),
  });

  const bulkDeleteMaintMutation = useMutation({
    mutationFn: async ({ maintIds, ticketIds }) => {
      const tasks = [];
      if (maintIds.length)   tasks.push(maintenanceApi.bulkDelete(maintIds));
      if (ticketIds.length)  tasks.push(ticketsApi.bulkDelete(ticketIds));
      await Promise.all(tasks);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance-dashboard"] });
      qc.invalidateQueries({ queryKey: ["maintenance-dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      setSelectedMaintItems(new Set());
      setShowBulkDeleteMaint(false);
    },
    onError: (err) => {
      qc.invalidateQueries({ queryKey: ["maintenance-dashboard"] });
      qc.invalidateQueries({ queryKey: ["maintenance-dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      addNotification(err?.response?.data?.detail || "שגיאה במחיקת המשימות", "error");
    },
  });

  const mergeMaintMutation = useMutation({
    mutationFn: ({ type, primaryId, secondaryIds }) =>
      type === "maintenance"
        ? maintenanceApi.merge(primaryId, secondaryIds)
        : ticketsApi.merge(primaryId, secondaryIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance-dashboard"] });
      qc.invalidateQueries({ queryKey: ["tickets-maintenance-queue"] });
      setSelectedMaintItems(new Set());
      setShowMergeMaint(false);
      setMergePrimaryMaint(null);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באיחוד המשימות", "error"),
  });

  // ── Tab change ─────────────────────────────────────────────────────────────
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setActiveFilter(null);
    setSelectedProjectItems(new Set());
    setSelectedMaintItems(new Set());
  };

  // ── Tab counts ─────────────────────────────────────────────────────────────
  const ticketOpenCount = tickets.filter((t) => _OPEN_STATUSES_DASH.includes(t.status)).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="space-y-6" dir="rtl">

      {/* Crisis mode banner */}
      {_crisisActive && (
        <div className="rounded-lg bg-red-50 border border-red-300 px-4 py-2.5 flex items-center gap-3 text-sm text-red-700">
          <span>⚠️</span>
          <span className="font-medium">מצב חירום פעיל — ביצוע פעולות AI מושהה</span>
          <a href="/agents" className="mr-auto text-xs underline hover:text-red-900">עבור לניהול סוכנים</a>
        </div>
      )}
      {/* A failed crisis-mode load is not the same as "confirmed not in crisis" -- say so instead
          of silently showing nothing, which would hide the one moment this warning matters most. */}
      {!_crisisActive && _crisisModeFailed && (
        <div role="alert" className="rounded-lg bg-amber-50 border border-amber-300 px-4 py-2.5 flex items-center gap-3 text-sm text-amber-700">
          <span>⚠️</span>
          <span className="font-medium">לא ניתן לבדוק אם מצב חירום פעיל — בדוק ידנית בניהול סוכנים</span>
          <a href="/agents" className="mr-auto text-xs underline hover:text-amber-900">עבור לניהול סוכנים</a>
        </div>
      )}

      {/* Header + tabs */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">לוח בקרה</h1>
        <div className="flex border-b border-gray-200">
          {QUEUE_TABS.map((tab) => {
            let count = null;
            if (tab.id === "tickets")     count = isLoading ? null : (ticketsFailed ? "!" : ticketOpenCount);
            if (tab.id === "projects")    count = (companyFilter || technicianFilter) ? computedProjectStats.total_active : (isLoadingProjectStats ? null : (projectStatsFailed ? "!" : (projectStats?.total_active ?? 0)));
            if (tab.id === "maintenance") count = (companyFilter || technicianFilter) ? computedMaintStats.total_active : (isLoadingMaintenanceStats ? null : (maintenanceStatsFailed ? "!" : (maintenanceStats?.total_active ?? 0)));
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? "border-current font-semibold"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                style={activeTab === tab.id ? { color: tab.color, borderColor: tab.color } : {}}
              >
                {tab.label}
                {count !== null && (
                  <span
                    className={`text-xs font-bold px-1.5 py-0.5 rounded-full leading-none ${
                      activeTab === tab.id ? "bg-current/10" : "bg-gray-100 text-gray-500"
                    }`}
                    style={activeTab === tab.id ? { color: tab.color } : {}}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dashboard controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setActiveFilter(null); }}
            className="border rounded-lg px-3 py-2 text-sm bg-white text-gray-700 h-10 min-w-40"
          >
            <option value="">כל החברות</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={technicianFilter}
            onChange={(e) => { setTechnicianFilter(e.target.value); setActiveFilter(null); }}
            className="border rounded-lg px-3 py-2 text-sm bg-white text-gray-700 h-10 min-w-40"
          >
            <option value="">כל הטכנאים</option>
            {technicians.map((tech) => (
              <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
            ))}
          </select>
          {(companyFilter || technicianFilter) && (
            <button
              onClick={() => {
                setCompanyFilter("");
                setTechnicianFilter("");
                setActiveFilter(null);
              }}
              className="text-xs text-gray-400 hover:text-gray-600 border rounded px-2 py-1 hover:bg-gray-50 transition-colors"
            >
              נקה סינון ✕
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 border rounded-lg bg-white px-3 h-10">
            <span className="text-xs text-gray-400">הצג</span>
            <select
              value={dashPageSize}
              onChange={(e) => handleDashPageSize(Number(e.target.value))}
              className="text-sm text-gray-700 bg-white cursor-pointer focus:outline-none"
            >
              {DASH_PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-xs text-gray-400">בדף</span>
          </div>
          <button
            onClick={() => setShowCreateTicket((v) => !v)}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors h-10"
          >
            + קריאה חדשה
          </button>
        </div>
      </div>

      {showCreateTicket && (
        <TicketCreateForm
          form={ticketForm}
          setForm={setTicketForm}
          companies={companies}
          contacts={contacts}
          technicians={technicians}
          priorityOptions={TICKET_PRIORITY_OPTIONS}
          createMutation={createTicketMutation}
          onSubmit={handleTicketSubmit}
          onCancel={() => { setShowCreateTicket(false); setTicketForm(EMPTY_TICKET_FORM); }}
        />
      )}

      {/* ── KPI cards ──────────────────────────────────────────────────────── */}

      {activeTab === "tickets" && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {STAT_CARDS.map((card) => {
            const count    = tickets.filter(card.filter).length;
            const isActive = activeFilter === card.key;
            const cardStyle = { flex: '0 0 auto', width: 90, padding: '6px 10px' };
            if (card.noFilter) {
              return (
                <button
                  key={card.key}
                  onClick={() => setActiveFilter(null)}
                  style={cardStyle}
                  className={`rounded-xl border text-center transition-all cursor-pointer ${card.color} ${
                    activeFilter === null ? `ring-2 ${card.ring} shadow-md scale-[1.03]` : "hover:shadow-md"
                  }`}
                >
                  <p style={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>{isLoading ? "—" : ticketsFailed ? "⚠" : count}</p>
                  <p style={{ fontSize: 10, fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{card.label}</p>
                </button>
              );
            }
            return (
              <button
                key={card.key}
                onClick={() => handleCardClick(card.key)}
                style={cardStyle}
                className={`rounded-xl border text-center transition-all cursor-pointer ${card.color} ${
                  isActive ? `ring-2 ${card.ring} shadow-md scale-[1.03]` : "hover:shadow-md"
                }`}
              >
                <p style={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>{isLoading ? "—" : count}</p>
                <p style={{ fontSize: 10, fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{card.label}</p>
              </button>
            );
          })}
        </div>
      )}

      {activeTab === "projects" && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {[
            { label: 'סה"כ פעילים', value: computedProjectStats.total_active, color: "border-indigo-300 bg-indigo-50 text-indigo-700" },
            { label: "באיחור",      value: computedProjectStats.overdue,       color: "border-red-300 bg-red-50 text-red-700" },
            { label: "לא הוקצו",   value: computedProjectStats.unassigned,    color: "border-orange-200 bg-orange-50 text-orange-700" },
            { label: "המתנה",      value: computedProjectStats.waiting,       color: "border-purple-200 bg-purple-50 text-purple-700" },
            { label: "בטיפול",     value: computedProjectStats.in_progress,   color: "border-yellow-200 bg-yellow-50 text-yellow-700" },
          ].map((card) => (
            <div key={card.label} style={{ flex: '0 0 auto', width: 90, padding: '6px 10px' }} className={`rounded-xl border text-center ${card.color}`}>
              <p style={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>
                {(isLoadingProjects || isLoadingProjectTickets) ? "—" : (card.value ?? 0)}
              </p>
              <p style={{ fontSize: 10, fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{card.label}</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === "maintenance" && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {[
            { label: 'סה"כ פעילות',    value: maintStat("total_active"),        color: "border-indigo-300 bg-indigo-50 text-indigo-700" },
            { label: "דחופות",          value: maintStat("urgent"),              color: "border-red-300 bg-red-50 text-red-700" },
            { label: "לא הוקצו",       value: maintStat("unassigned"),          color: "border-orange-200 bg-orange-50 text-orange-700" },
            { label: "המתנה",          value: maintStat("waiting"),             color: "border-purple-200 bg-purple-50 text-purple-700" },
            { label: "הושלמו החודש",   value: maintStat("completed_this_month"), color: "border-green-300 bg-green-50 text-green-700" },
          ].map((card) => (
            <div key={card.label} style={{ flex: '0 0 auto', width: 90, padding: '6px 10px' }} className={`rounded-xl border text-center ${card.color}`}>
              <p style={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1 }}>
                {maintStatsLoading ? "—" : (card.value ?? 0)}
              </p>
              <p style={{ fontSize: 10, fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{card.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── AI Operations ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm px-4 py-3" dir="rtl">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">פעילות AI</span>
            {aiPendingCount > 0 && (
              <Link
                to="/agent-runs?status=pending_review"
                className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full hover:bg-orange-200 transition-colors"
              >
                {aiPendingCount} ממתינים לאישור
              </Link>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-brand-600">
            <Link to="/agents" className="hover:underline">סוכנים</Link>
            <Link to="/skills" className="hover:underline">מיומנויות</Link>
            <Link to="/feedback" className="hover:underline">איכות AI</Link>
            <Link to="/scheduled-checks" className="hover:underline">בדיקות מתוזמנות</Link>
            {alertsFailed ? (
              // a failed evaluation is not "zero alerts": say the count is unknown
              <span className="flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold" title="לא ניתן לטעון את ספירת ההתראות">
                ⚠️ התראות לא נטענו
              </span>
            ) : _activeAlertCount > 0 && (
              <Link to="/alerts" className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold hover:bg-red-200">
                🚨 {_activeAlertCount} התראות
              </Link>
            )}
          </div>
        </div>
        {_recentRuns.length === 0 ? (
          <p className="text-xs text-gray-400">אין הרצות אחרונות</p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {_recentRuns.map((run) => {
              const statusCls =
                run.status === "completed" || run.status === "resolved" || run.status === "approved" || run.status === "connection_active" ? "bg-green-100 text-green-700" :
                run.status === "failed"    || run.status === "rejected"  ? "bg-red-100 text-red-700" :
                run.status === "awaiting_technician_review" || run.status === "awaiting_approval" ? "bg-orange-100 text-orange-700" :
                "bg-blue-100 text-blue-700";
              const AI_STATUS_HE = {
                completed: "הושלם", resolved: "נפתר", approved: "אושר",
                failed: "נכשל", rejected: "נדחה",
                awaiting_technician_review: "ממתין לסקירה", awaiting_approval: "ממתין לאישור",
                analyzing: "מנתח", pending: "מאתחל", diagnosing: "מאבחן", executing: "מבצע",
                paused: "הופסק", connection_active: "חיבור פעיל",
                waiting_for_customer_connection: "ממתין לחיבור",
                waiting_for_customer_answer: "ממתין לתשובה",
              };
              const statusLabel = AI_STATUS_HE[run.status] ?? run.status;
              return (
                <Link
                  key={run.id}
                  to={`/agent-runs/${run.id}`}
                  className="flex items-center gap-1.5 border rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition-colors min-w-0"
                >
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${statusCls}`}>{statusLabel}</span>
                  <span className="text-xs text-gray-700 truncate max-w-32">{_agentNameMap[run.agent_id] || "סוכן"}</span>
                  {run.ticket_number && <span className="text-xs text-gray-400 shrink-0">#{run.ticket_number}</span>}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Tables ─────────────────────────────────────────────────────────── */}

      {/* Tickets table */}
      {activeTab === "tickets" && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-3 xl:px-4 2xl:px-5 py-3 xl:py-4 border-b flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-gray-700">
                {activeCard ? `קריאות — ${activeCard.label}` : "קריאות אחרונות"}
              </h2>
              {activeCard && (
                <button
                  onClick={() => setActiveFilter(null)}
                  className="text-xs text-gray-400 hover:text-gray-600 border rounded px-2 py-0.5 transition-colors"
                >
                  הצג הכל ✕
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Link to="/tickets" className="text-xs text-brand-600 hover:underline">
                מעבר לרשימה המלאה
              </Link>
            </div>
          </div>

          {/* Bulk action toolbar */}
          <div className="px-3 xl:px-4 2xl:px-5 py-2.5 border-b bg-blue-50 flex items-center gap-2 xl:gap-3 flex-wrap">
            <span className="text-sm font-medium text-blue-700">
              {selectedTickets.size === 0 ? "לא נבחרו קריאות" : `נבחרו ${selectedTickets.size} קריאות`}
            </span>
            <button
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={selectedTickets.size === 0 || bulkDeleteMutation.isPending}
              className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              מחק
            </button>
            <button
              onClick={() => setShowMergeDialog(true)}
              disabled={selectedTickets.size < 2 || mergeMutation.isPending}
              title={selectedTickets.size < 2 ? "יש לבחור לפחות 2 קריאות לאיחוד" : ""}
              className="text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              איחוד
            </button>
            <button
              onClick={() => {
                const t = sorted.find((t) => selectedTickets.has(t.id));
                if (t) {
                  setSplitForm({ new_subject: `FW: ${t.title}`, note: "", technician_id: "" });
                  setShowSplitDialog(true);
                }
              }}
              disabled={selectedTickets.size !== 1 || splitMutation.isPending}
              title={selectedTickets.size !== 1 ? "יש לבחור קריאה אחת בדיוק לפיצול" : ""}
              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              פצל
            </button>
            <button
              onClick={() => setShowBulkCloseConfirm(true)}
              disabled={selectedTickets.size === 0 || bulkCloseMutation.isPending}
              className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              סגור
            </button>
            <button
              onClick={() => setShowBulkAssignDialog(true)}
              disabled={selectedTickets.size === 0 || bulkAssignMutation.isPending}
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              הקצה לנציג
            </button>
            <button
              onClick={() => setSelectedTickets(new Set())}
              disabled={selectedTickets.size === 0}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              בטל בחירה
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-brand-600" />
            </div>
          ) : displayed.length === 0 ? (
            <p className="text-center py-10 text-gray-400 text-sm">אין קריאות בקטגוריה זו</p>
          ) : (
            <>
            {sorted.length > dashPageSize && (
              <div className="px-3 xl:px-4 2xl:px-5 py-1.5 text-xs text-gray-400 bg-gray-50 border-b">
                מוצגים {dashPageSize} מתוך {sorted.length} קריאות
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
            <table className="w-full table-fixed text-xs xl:text-sm" style={{ width: '100%' }}>
              <thead className="bg-gray-50 border-b text-gray-500 text-xs">
                <tr>
                  {/* No select-all: tickets may only be selected one at a time */}
                  <th className="px-2 xl:px-3 py-2.5" style={{ width: 36 }} />
                  <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 55 }}>#</th>
                  <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 400 }}>כותרת</th>
                  <th className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 160 }} onClick={() => handleSort("sender")}>שולח{si("sender")}</th>
                  <th className="text-right px-2 xl:px-3 py-2.5 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 130 }} onClick={() => handleSort("company")}>חברה{si("company")}</th>
                  <th className="text-right px-2 xl:px-3 py-2.5 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 140 }} onClick={() => handleSort("technician")}>טכנאי{si("technician")}</th>
                  <th className="text-right px-2 xl:px-3 py-2.5 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 100 }} onClick={() => handleSort("status")}>סטטוס{si("status")}</th>
                  <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 80 }}>SLA</th>
                  <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 135 }}>Due Date</th>
                  <th className="text-right px-2 xl:px-3 py-2.5 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 110 }} onClick={() => handleSort("opened_at")}>תאריך פתיחה{si("opened_at")}</th>
                  <th className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 110 }} onClick={() => handleSort("updated_at")}>עדכון אחרון{si("updated_at")}</th>
                  <th className="px-1 py-2.5" style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {sorted.slice(0, dashPageSize).map((t) => (
                  <tr key={t.id} className={`border-b last:border-0 hover:bg-gray-50 transition-colors ${isTicketOverdue(t) ? "bg-red-50 hover:bg-red-100" : selectedTickets.has(t.id) ? "bg-blue-50" : ""}`}>
                    <td className="px-2 xl:px-3 py-2.5">
                      <input
                        type="checkbox"
                        className="accent-brand-600 w-3.5 h-3.5"
                        checked={selectedTickets.has(t.id)}
                        onChange={(e) => {
                          setSelectedTickets((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(t.id);
                            else next.delete(t.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 text-gray-400 text-xs font-mono" style={{ overflow: 'hidden' }}>
                      <div className="inline-flex items-center gap-1 whitespace-nowrap">
                        <span>{t.ticket_number}</span>
                        {ticketHasAttachments(t) && (
                          <span title={`${attachmentCount(t)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-400 text-sm leading-none flex-shrink-0">
                            📎
                            <span className="text-[10px] font-semibold leading-none">{attachmentCount(t)}</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 font-medium" style={{ overflow: 'hidden' }}>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Link
                          to={`/tickets/${t.id}?from=${encodeURIComponent(activeFilter ? `/dashboard?filter=${activeFilter}` : "/dashboard")}`}
                          state={{ ticketIds: visibleTicketIds }}
                          className="text-brand-600 hover:underline truncate"
                          title={t.title}
                        >
                          {t.title}
                        </Link>
                        {t.sender_email === "system@prodigy.internal" && (
                          <span title="בדיקה מתוזמנת — לא קריאת לקוח" className="text-xs text-violet-600 font-medium bg-violet-50 border border-violet-200 rounded px-1 py-0.5 flex-shrink-0">🕐 מתוזמן</span>
                        )}
                        {t.has_customer_reply && (
                          <span title="הלקוח השיב למייל" className="text-orange-500 text-sm leading-none flex-shrink-0">✉</span>
                        )}
                      </div>
                    </td>
                    <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5 text-gray-600 text-xs" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.sender_name}</td>
                    <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                      <select
                        value={t.company_id ?? ""}
                        onChange={(e) =>
                          inlineUpdate.mutate({ id: t.id, data: { company_id: e.target.value || null } })
                        }
                        className="border rounded px-1.5 py-0.5 text-xs text-gray-700 bg-white w-full"
                      >
                        <option value="">—</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                      <select
                        value={t.technician_id ?? ""}
                        onChange={(e) =>
                          inlineUpdate.mutate({ id: t.id, data: { technician_id: e.target.value === "" ? null : e.target.value } })
                        }
                        className="border rounded px-1.5 py-0.5 text-xs text-gray-700 bg-white w-full"
                      >
                        <option value="">לא משובץ</option>
                        {t.technician_id && !technicians.some((tech) => tech.id === t.technician_id) && (
                          <option value={t.technician_id}>
                            {t.technician ? `${t.technician.first_name} ${t.technician.last_name}` : t.technician_id}
                          </option>
                        )}
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.first_name} {tech.last_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                      <select
                        value={t.status}
                        onChange={(e) => inlineUpdate.mutate({ id: t.id, data: { status: e.target.value } })}
                        className={`border rounded px-1.5 py-0.5 text-xs font-semibold w-full ${STATUS_BADGE[t.status]}`}
                      >
                        {ticketStatusSelectOptions(t.status).map((opt) => (
                          <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                      {t.is_sla_breach && (
                        <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                          חריגה
                        </span>
                      )}
                    </td>
                  <td className="px-2 xl:px-3 py-2.5 text-xs">
                    <input aria-label={`מועד יעד לקריאה ${t.ticket_number}`} type="date" value={t.due_date?.slice(0, 10) || ""} onChange={(e) => inlineUpdate.mutate({ id: t.id, data: { due_date: e.target.value || null } })} className={`w-full rounded border px-1 py-0.5 ${isTicketOverdue(t) ? "border-red-400 text-red-700 bg-red-50" : "border-gray-200"}`} />
                    {isTicketOverdue(t) && <span role="img" aria-label="איחור בביצוע" title="המועד עבר והקריאה טרם נסגרה">😞</span>}
                  </td>
                    <td className="px-2 xl:px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap" style={{ overflow: 'hidden' }}>
                      {fmtDateTime(t.opened_at)}
                    </td>
                    <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap" style={{ overflow: 'hidden' }}>
                      {fmtDateTime(t.updated_at)}
                    </td>
                    <td className="px-2 xl:px-3 py-2.5">
                      <button
                        onClick={() => confirmTicketDeletion(t, window.confirm, deleteMutation.mutate)}
                        disabled={deleteMutation.isPending && deleteMutation.variables === t.id}
                        className="text-gray-300 hover:text-red-500 transition-colors text-base leading-none disabled:opacity-40"
                        title="מחק קריאה"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            </>
          )}
        </div>
      )}

      {/* ── Projects unified table ─────────────────────────────────────────────── */}
      {activeTab === "projects" && (() => {
        const mergedProjects = [
          ...projects.map((p) => ({
            _type: "project", id: p.id, key: `project:${p.id}`,
            title: p.title, company: p.company,
            person: p.assignee?.display_name || "—",
            statusBadge: QUEUE_STATUS_BADGE[p.status] || "bg-gray-100 text-gray-600",
            statusLabel: QUEUE_STATUS_HE[p.status] || p.status,
            updated_at: p.updated_at,
          })),
          ...projectTickets.map((t) => ({
            _type: "ticket", id: t.id, key: `ticket:${t.id}`,
            title: t.title, company: t.company,
            person: t.sender_name,
            statusBadge: STATUS_BADGE[t.status] || "bg-gray-100 text-gray-500",
            statusLabel: ticketStatusLabel(t.status),
            updated_at: t.updated_at,
            ticket_number: t.ticket_number, has_reply: t.has_customer_reply,
            attachments_count: t.attachments_count,
            has_attachments: t.has_attachments,
          })),
        ].sort((a, b) => new Date(asUtc(b.updated_at)) - new Date(asUtc(a.updated_at)));

        const selTypes = new Set([...selectedProjectItems].map(k => k.split(":")[0]));
        const canMergeP = selTypes.size === 1 && selectedProjectItems.size >= 2;
        const slicedProjects = mergedProjects.slice(0, dashPageSize);
        const allSel = slicedProjects.length > 0 && slicedProjects.every(r => selectedProjectItems.has(r.key));

        return (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-3 xl:px-4 2xl:px-5 py-3 xl:py-4 border-b flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-gray-700">פרויקטים</h2>
                <span className="text-xs text-gray-400">{mergedProjects.length} פריטים</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowCreateProject(true)} className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                  + פרויקט חדש
                </button>
              </div>
            </div>

            <div className="px-3 xl:px-4 2xl:px-5 py-2.5 border-b bg-purple-50 flex items-center gap-2 xl:gap-3 flex-wrap">
              <span className="text-sm font-medium text-purple-700">
                {selectedProjectItems.size === 0 ? "לא נבחרו פריטים" : `נבחרו ${selectedProjectItems.size} פריטים`}
              </span>
              <button
                onClick={() => setShowBulkDeleteProjects(true)}
                disabled={selectedProjectItems.size === 0}
                className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >מחק</button>
              <button
                onClick={() => { setMergePrimaryProject(null); setShowMergeProjects(true); }}
                disabled={!canMergeP}
                title={!canMergeP ? (selectedProjectItems.size < 2 ? "יש לבחור לפחות 2 פריטים" : "איחוד רק עבור פריטים מאותו סוג") : ""}
                className="text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >איחוד</button>
              <button
                onClick={() => setSelectedProjectItems(new Set())}
                disabled={selectedProjectItems.size === 0}
                className="mr-auto text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >נקה</button>
            </div>

            {(isLoadingProjects || isLoadingProjectTickets) ? (
              <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-purple-600" /></div>
            ) : mergedProjects.length === 0 ? (
              <p className="text-center py-10 text-gray-400 text-sm">אין פריטים בקטגוריה זו</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <table className="w-full table-fixed text-xs xl:text-sm" style={{ width: '100%' }}>
                <thead className="bg-gray-50 border-b text-gray-500 text-xs">
                  <tr>
                    <th className="px-2 xl:px-3 py-2.5" style={{ width: 36 }}>
                      <input type="checkbox" className="accent-purple-600 w-3.5 h-3.5"
                        checked={allSel}
                        onChange={(e) => setSelectedProjectItems(e.target.checked ? new Set(slicedProjects.map(r => r.key)) : new Set())} />
                    </th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 60 }}>#</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 424 }}>כותרת</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 200 }}>חברה</th>
                    <th className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5" style={{ width: 250 }}>אחראי / שולח</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 105 }}>סטטוס</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 85 }}>סוג</th>
                    <th className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5" style={{ width: 110 }}>עדכון אחרון</th>
                  </tr>
                </thead>
                <tbody>
                  {slicedProjects.map((row, idx) => (
                    <tr key={row.key}
                      onClick={() => navigate(row._type === "project" ? `/projects/${row.id}` : `/tickets/${row.id}`)}
                      className={`border-b last:border-0 hover:bg-purple-50 transition-colors cursor-pointer ${selectedProjectItems.has(row.key) ? "bg-purple-50" : ""}`}>
                      <td className="px-2 xl:px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="accent-purple-600 w-3.5 h-3.5"
                          checked={selectedProjectItems.has(row.key)}
                          onChange={(e) => setSelectedProjectItems(prev => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(row.key) : next.delete(row.key);
                            return next;
                          })} />
                      </td>
                      <td className="px-2 xl:px-3 py-2.5 text-gray-400 text-xs font-mono" style={{ overflow: 'hidden' }}>
                        <div className="inline-flex items-center gap-1 whitespace-nowrap">
                          <span>{row._type === "ticket" ? `#${row.ticket_number}` : idx + 1}</span>
                          {row._type === "ticket" && ticketHasAttachments(row) && (
                            <span title={`${attachmentCount(row)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-400 text-sm leading-none flex-shrink-0">
                              📎
                              <span className="text-[10px] font-semibold leading-none">{attachmentCount(row)}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 xl:px-3 py-2.5 font-medium text-gray-800" style={{ overflow: 'hidden' }}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="truncate" title={row.title}>{row.title}</span>
                          {row.has_reply && <span className="text-orange-500 text-sm leading-none flex-shrink-0">✉</span>}
                        </div>
                      </td>
                      <td className="px-2 xl:px-3 py-2.5 text-gray-600 text-xs" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.company?.name || "—"}</td>
                      <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5 text-gray-600 text-xs" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.person}</td>
                      <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${row.statusBadge}`}>{row.statusLabel}</span>
                      </td>
                      <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${row._type === "project" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                          {row._type === "project" ? "פרויקט" : "קריאה"}
                        </span>
                      </td>
                      <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap" style={{ overflow: 'hidden' }}>{fmtDateTime(row.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Maintenance unified table ───────────────────────────────────────────── */}
      {activeTab === "maintenance" && (() => {
        const mergedMaint = [
          ...maintenanceItems.map((m) => {
            const cat = CATEGORY_BADGE[m.category];
            return {
              _type: "maintenance", id: m.id, key: `maintenance:${m.id}`,
              title: m.title, company: m.company,
              catLabel: cat ? cat.label : (m.category || "—"), catCls: cat ? cat.cls : "bg-gray-100 text-gray-600 border border-gray-200",
              statusBadge: QUEUE_STATUS_BADGE[m.status] || "bg-gray-100 text-gray-600",
              statusLabel: QUEUE_STATUS_HE[m.status] || m.status,
              updated_at: m.updated_at || m.due_date,
            };
          }),
          ...maintenanceTickets.map((t) => {
            const cat = CATEGORY_BADGE[t.ai_category];
            return {
              _type: "ticket", id: t.id, key: `ticket:${t.id}`,
              title: t.title, company: t.company,
              catLabel: cat ? cat.label : (AI_CATEGORY_HE[t.ai_category] || t.ai_category || "—"), catCls: cat ? cat.cls : "bg-gray-100 text-gray-600 border border-gray-200",
              statusBadge: STATUS_BADGE[t.status] || "bg-gray-100 text-gray-500",
              statusLabel: ticketStatusLabel(t.status),
              updated_at: t.updated_at,
              ticket_number: t.ticket_number, has_reply: t.has_customer_reply,
              attachments_count: t.attachments_count,
              has_attachments: t.has_attachments,
            };
          }),
        ].sort((a, b) => new Date(asUtc(b.updated_at)) - new Date(asUtc(a.updated_at)));

        const selMTypes = new Set([...selectedMaintItems].map(k => k.split(":")[0]));
        const canMergeM = selMTypes.size === 1 && selectedMaintItems.size >= 2;
        const slicedMaint = mergedMaint.slice(0, dashPageSize);
        const allSelM = slicedMaint.length > 0 && slicedMaint.every(r => selectedMaintItems.has(r.key));

        return (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-3 xl:px-4 2xl:px-5 py-3 xl:py-4 border-b flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-gray-700">תחזוקה</h2>
                <span className="text-xs text-gray-400">{mergedMaint.length} פריטים</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowCreateMaint(true)} className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                  + משימה חדשה
                </button>
              </div>
            </div>

            <div className="px-3 xl:px-4 2xl:px-5 py-2.5 border-b bg-green-50 flex items-center gap-2 xl:gap-3 flex-wrap">
              <span className="text-sm font-medium text-green-700">
                {selectedMaintItems.size === 0 ? "לא נבחרו פריטים" : `נבחרו ${selectedMaintItems.size} פריטים`}
              </span>
              <button
                onClick={() => setShowBulkDeleteMaint(true)}
                disabled={selectedMaintItems.size === 0}
                className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >מחק</button>
              <button
                onClick={() => { setMergePrimaryMaint(null); setShowMergeMaint(true); }}
                disabled={!canMergeM}
                title={!canMergeM ? (selectedMaintItems.size < 2 ? "יש לבחור לפחות 2 פריטים" : "איחוד רק עבור פריטים מאותו סוג") : ""}
                className="text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >איחוד</button>
              <button
                onClick={() => setSelectedMaintItems(new Set())}
                disabled={selectedMaintItems.size === 0}
                className="mr-auto text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >נקה</button>
            </div>

            {(isLoadingMaintenance || isLoadingMaintenanceTickets) ? (
              <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-green-600" /></div>
            ) : mergedMaint.length === 0 ? (
              <p className="text-center py-10 text-gray-400 text-sm">אין פריטים בקטגוריה זו</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <table className="w-full table-fixed text-xs xl:text-sm" style={{ width: '100%' }}>
                <thead className="bg-gray-50 border-b text-gray-500 text-xs">
                  <tr>
                    <th className="px-2 xl:px-3 py-2.5" style={{ width: 36 }}>
                      <input type="checkbox" className="accent-green-600 w-3.5 h-3.5"
                        checked={allSelM}
                        onChange={(e) => setSelectedMaintItems(e.target.checked ? new Set(slicedMaint.map(r => r.key)) : new Set())} />
                    </th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 60 }}>#</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 424 }}>כותרת</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 200 }}>חברה</th>
                    <th className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5" style={{ width: 115 }}>קטגוריה</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 105 }}>סטטוס</th>
                    <th className="text-right px-2 xl:px-3 py-2.5" style={{ width: 85 }}>סוג</th>
                    <th className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5" style={{ width: 110 }}>עדכון אחרון</th>
                  </tr>
                </thead>
                <tbody>
                  {slicedMaint.map((row, idx) => (
                    <tr key={row.key}
                      onClick={() => navigate(row._type === "maintenance" ? `/maintenance/${row.id}` : `/tickets/${row.id}`)}
                      className={`border-b last:border-0 hover:bg-green-50 transition-colors cursor-pointer ${selectedMaintItems.has(row.key) ? "bg-green-50" : ""}`}>
                      <td className="px-2 xl:px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="accent-green-600 w-3.5 h-3.5"
                          checked={selectedMaintItems.has(row.key)}
                          onChange={(e) => setSelectedMaintItems(prev => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(row.key) : next.delete(row.key);
                            return next;
                          })} />
                      </td>
                      <td className="px-2 xl:px-3 py-2.5 text-gray-400 text-xs font-mono" style={{ overflow: 'hidden' }}>
                        <div className="inline-flex items-center gap-1 whitespace-nowrap">
                          <span>{row._type === "ticket" ? `#${row.ticket_number}` : idx + 1}</span>
                          {row._type === "ticket" && ticketHasAttachments(row) && (
                            <span title={`${attachmentCount(row)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-400 text-sm leading-none flex-shrink-0">
                              📎
                              <span className="text-[10px] font-semibold leading-none">{attachmentCount(row)}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 xl:px-3 py-2.5 font-medium text-gray-800" style={{ overflow: 'hidden' }}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="truncate" title={row.title}>{row.title}</span>
                          {row.has_reply && <span className="text-orange-500 text-sm leading-none flex-shrink-0">✉</span>}
                        </div>
                      </td>
                      <td className="px-2 xl:px-3 py-2.5 text-gray-600 text-xs" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.company?.name || "—"}</td>
                      <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${row.catCls}`}>{row.catLabel}</span>
                      </td>
                      <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${row.statusBadge}`}>{row.statusLabel}</span>
                      </td>
                      <td className="px-2 xl:px-3 py-2.5" style={{ overflow: 'hidden' }}>
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${row._type === "maintenance" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                          {row._type === "maintenance" ? "תחזוקה" : "קריאה"}
                        </span>
                      </td>
                      <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap" style={{ overflow: 'hidden' }}>{fmtDateTime(row.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })()}


    </div>

    {/* ── Dialogs (tickets only) ────────────────────────────────────────────── */}

    {showBulkDeleteConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
          <h3 className="text-base font-semibold text-gray-800">אישור מחיקה</h3>
          <p className="text-sm text-gray-600">
            האם למחוק {selectedTickets.size} קריאות? פעולה זו אינה הפיכה.
          </p>
          {bulkDeleteMutation.isError && (
            <p className="text-xs text-red-500">{bulkDeleteMutation.error?.response?.data?.detail || "שגיאה במחיקה"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowBulkDeleteConfirm(false)}
              className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={() => bulkDeleteMutation.mutate([...selectedTickets])}
              disabled={bulkDeleteMutation.isPending}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {bulkDeleteMutation.isPending ? "מוחק..." : "מחק"}
            </button>
          </div>
        </div>
      </div>
    )}

    {showBulkCloseConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
          <h3 className="text-base font-semibold text-gray-800">אישור סגירה</h3>
          <p className="text-sm text-gray-600">
            האם לסגור את כל הקריאות המסומנות?
          </p>
          {bulkCloseMutation.isError && (
            <p className="text-xs text-red-500">{bulkCloseMutation.error?.response?.data?.detail || "שגיאה בסגירת הקריאות"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowBulkCloseConfirm(false)}
              className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={() => bulkCloseMutation.mutate([...selectedTickets])}
              disabled={bulkCloseMutation.isPending}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {bulkCloseMutation.isPending ? "סוגר..." : "סגור"}
            </button>
          </div>
        </div>
      </div>
    )}

    {showBulkAssignDialog && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
          <h3 className="text-base font-semibold text-gray-800">הקצה לנציג</h3>
          <p className="text-sm text-gray-600">
            בחר טכנאי להקצאת {selectedTickets.size} הקריאות המסומנות.
          </p>
          <select
            value={bulkAssignTechnicianId}
            onChange={(e) => setBulkAssignTechnicianId(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">בחר טכנאי</option>
            {technicians.map((tech) => (
              <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
            ))}
          </select>
          {bulkAssignMutation.isError && (
            <p className="text-xs text-red-500">{bulkAssignMutation.error?.response?.data?.detail || "שגיאה בהקצאה"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setShowBulkAssignDialog(false);
                setBulkAssignTechnicianId("");
              }}
              className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={() => bulkAssignMutation.mutate({ ids: [...selectedTickets], technicianId: bulkAssignTechnicianId })}
              disabled={!bulkAssignTechnicianId || bulkAssignMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {bulkAssignMutation.isPending ? "מקצה..." : "הקצה"}
            </button>
          </div>
        </div>
      </div>
    )}

    {showSplitDialog && (() => {
      const sourceTicket = sorted.find((t) => selectedTickets.has(t.id));
      if (!sourceTicket) return null;
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl border p-6 w-[30rem] space-y-4" dir="rtl">
            <h3 className="text-base font-semibold text-gray-800">
              פיצול קריאה #{sourceTicket.ticket_number}
            </h3>
            <div>
              <label className="block text-xs text-gray-500 mb-1">נושא הקריאה החדשה</label>
              <input
                value={splitForm.new_subject}
                onChange={(e) => setSplitForm((f) => ({ ...f, new_subject: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">תוכן / הערה לקריאה החדשה</label>
              <textarea
                rows={4}
                value={splitForm.note}
                onChange={(e) => setSplitForm((f) => ({ ...f, note: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">שבץ לטכנאי</label>
              <select
                value={splitForm.technician_id}
                onChange={(e) => setSplitForm((f) => ({ ...f, technician_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">ללא שיבוץ</option>
                {technicians.map((tech) => (
                  <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
                ))}
              </select>
            </div>
            {splitMutation.isError && (
              <p className="text-xs text-red-500">{splitMutation.error?.response?.data?.detail || "שגיאה בפיצול"}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowSplitDialog(false)}
                className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
              >
                ביטול
              </button>
              <button
                onClick={() => splitMutation.mutate({
                  source_ticket_id: sourceTicket.id,
                  new_subject: splitForm.new_subject,
                  note: splitForm.note,
                  technician_id: splitForm.technician_id || null,
                })}
                disabled={!splitForm.new_subject.trim() || splitMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {splitMutation.isPending ? "מפצל..." : "פצל קריאה"}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* Create project dialog */}
    {showCreateProject && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl border p-6 w-[28rem] space-y-4" dir="rtl">
          <h3 className="text-base font-semibold text-gray-800">פרויקט חדש</h3>
          <div>
            <label className="block text-xs text-gray-500 mb-1">שם הפרויקט *</label>
            <input value={projectForm.title} onChange={(e) => setProjectForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="שם הפרויקט" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">חברה</label>
              <select value={projectForm.company_id} onChange={(e) => setProjectForm((f) => ({ ...f, company_id: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="">—</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">אחראי</label>
              <select value={projectForm.assigned_to} onChange={(e) => setProjectForm((f) => ({ ...f, assigned_to: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">עדיפות</label>
              <select value={projectForm.priority} onChange={(e) => setProjectForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="low">נמוכה</option>
                <option value="medium">בינונית</option>
                <option value="high">גבוהה</option>
                <option value="critical">קריטית</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">סטטוס</label>
              <select value={projectForm.status} onChange={(e) => setProjectForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="unassigned">לא הוקצה</option>
                <option value="open">פתוח</option>
                <option value="waiting">המתנה</option>
              </select>
            </div>
          </div>
          {createProjectMutation.isError && (
            <p className="text-xs text-red-500">{createProjectMutation.error?.response?.data?.detail || "שגיאה ביצירה"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreateProject(false)} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
            <button
              onClick={() => createProjectMutation.mutate({
                title: projectForm.title,
                company_id: projectForm.company_id || null,
                assigned_to: projectForm.assigned_to || null,
                priority: projectForm.priority,
                status: projectForm.status,
              })}
              disabled={!projectForm.title.trim() || createProjectMutation.isPending}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {createProjectMutation.isPending ? "יוצר..." : "צור פרויקט"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Create maintenance dialog */}
    {showCreateMaint && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl border p-6 w-[32rem] space-y-4" dir="rtl">
          <h3 className="text-base font-semibold text-gray-800">משימת תחזוקה חדשה</h3>
          <div>
            <label className="block text-xs text-gray-500 mb-1">שם המשימה *</label>
            <input value={maintForm.title} onChange={(e) => setMaintForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="שם המשימה" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">קטגוריה *</label>
              <select value={maintForm.category} onChange={(e) => setMaintForm((f) => ({ ...f, category: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="backups">גיבויים</option>
                <option value="m365">M365</option>
                <option value="av_edr">AV/EDR</option>
                <option value="updates">עדכונים ותיקונים</option>
                <option value="firewall">חומת אש</option>
                <option value="email_security">אבטחת מייל</option>
                <option value="reports">דוחות</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">עדיפות</label>
              <select value={maintForm.priority} onChange={(e) => setMaintForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="low">נמוכה</option>
                <option value="medium">בינונית</option>
                <option value="high">גבוהה</option>
                <option value="critical">קריטית</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">חברה</label>
              <select value={maintForm.company_id} onChange={(e) => setMaintForm((f) => ({ ...f, company_id: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="">—</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">אחראי</label>
              <select value={maintForm.assigned_to} onChange={(e) => setMaintForm((f) => ({ ...f, assigned_to: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">תאריך יעד</label>
              <input type="date" value={maintForm.due_date} onChange={(e) => setMaintForm((f) => ({ ...f, due_date: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id="recurring-toggle" checked={maintForm.recurring}
                onChange={(e) => setMaintForm((f) => ({ ...f, recurring: e.target.checked }))}
                className="accent-green-600 w-4 h-4" />
              <label htmlFor="recurring-toggle" className="text-sm text-gray-700">משימה חוזרת</label>
            </div>
          </div>
          {maintForm.recurring && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">תדירות</label>
              <select value={maintForm.recurrence_pattern} onChange={(e) => setMaintForm((f) => ({ ...f, recurrence_pattern: e.target.value }))}
                className="w-full border rounded-lg px-2 py-2 text-sm">
                <option value="daily">יומי</option>
                <option value="weekly">שבועי</option>
                <option value="monthly">חודשי</option>
                <option value="quarterly">רבעוני</option>
              </select>
            </div>
          )}
          {createMaintMutation.isError && (
            <p className="text-xs text-red-500">{createMaintMutation.error?.response?.data?.detail || "שגיאה ביצירה"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreateMaint(false)} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
            <button
              onClick={() => createMaintMutation.mutate({
                title: maintForm.title,
                category: maintForm.category,
                company_id: maintForm.company_id || null,
                assigned_to: maintForm.assigned_to || null,
                priority: maintForm.priority,
                due_date: maintForm.due_date || null,
                recurring: maintForm.recurring,
                recurrence_pattern: maintForm.recurring ? maintForm.recurrence_pattern || null : null,
              })}
              disabled={!maintForm.title.trim() || createMaintMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {createMaintMutation.isPending ? "יוצר..." : "צור משימה"}
            </button>
          </div>
        </div>
      </div>
    )}

    {showMergeDialog && (() => {
      const selectedList = sorted.filter((t) => selectedTickets.has(t.id));
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl border p-6 w-96 space-y-4" dir="rtl">
            <h3 className="text-base font-semibold text-gray-800">איחוד קריאות</h3>
            <p className="text-sm text-gray-600">בחר את הקריאה הראשית שתקלוט את שאר הקריאות:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {selectedList.map((t) => (
                <button
                  key={t.id}
                  onClick={() =>
                    mergeMutation.mutate({
                      primaryId: t.id,
                      secondaryIds: selectedList.filter((s) => s.id !== t.id).map((s) => s.id),
                    })
                  }
                  disabled={mergeMutation.isPending}
                  className="w-full text-right px-3 py-2.5 rounded-lg border hover:border-teal-400 hover:bg-teal-50 transition-colors disabled:opacity-50"
                >
                  <div className="text-sm font-medium text-gray-800 truncate">
                    #{t.ticket_number} — {t.title}
                  </div>
                  <div className="text-xs text-gray-400">{t.sender_name}</div>
                </button>
              ))}
            </div>
            {mergeMutation.isError && (
              <p className="text-xs text-red-500">{mergeMutation.error?.response?.data?.detail || "שגיאה באיחוד"}</p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => setShowMergeDialog(false)}
                className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* ── Bulk delete projects confirm ─────────────────────────────────────── */}
    {showBulkDeleteProjects && (() => {
      const keys = [...selectedProjectItems];
      const projectIds = keys.filter(k => k.startsWith("project:")).map(k => k.split(":")[1]);
      const ticketIds  = keys.filter(k => k.startsWith("ticket:")).map(k => k.split(":")[1]);
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
            <h3 className="text-base font-semibold text-gray-800">אישור מחיקה</h3>
            <p className="text-sm text-gray-600">האם למחוק {selectedProjectItems.size} פריטים? פעולה זו אינה הפיכה.</p>
            {bulkDeleteProjectsMutation.isError && (
              <p className="text-xs text-red-500">{bulkDeleteProjectsMutation.error?.response?.data?.detail || "שגיאה במחיקה"}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowBulkDeleteProjects(false)} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
              <button onClick={() => bulkDeleteProjectsMutation.mutate({ projectIds, ticketIds })}
                disabled={bulkDeleteProjectsMutation.isPending}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
                {bulkDeleteProjectsMutation.isPending ? "מוחק..." : "מחק"}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* ── Merge projects dialog ────────────────────────────────────────────── */}
    {showMergeProjects && (() => {
      const keys = [...selectedProjectItems];
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl border p-6 w-[26rem] space-y-4" dir="rtl">
            <h3 className="text-base font-semibold">איחוד פריטים</h3>
            <p className="text-sm text-gray-600">בחר את הפריט הראשי שאליו יאוחדו שאר הפריטים:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {keys.map(k => {
                const [type, id] = k.split(":");
                const row = [...projects, ...projectTickets].find(r => r.id === id);
                return (
                  <label key={k} className="flex items-center gap-2.5 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                    <input type="radio" name="merge-primary-proj" value={k}
                      checked={mergePrimaryProject === k}
                      onChange={() => setMergePrimaryProject(k)}
                      className="accent-purple-600" />
                    <span className="text-sm flex-1">{row?.title || id}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${type === "project" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                      {type === "project" ? "פרויקט" : "קריאה"}
                    </span>
                  </label>
                );
              })}
            </div>
            {mergeProjectsMutation.isError && (
              <p className="text-xs text-red-500">{mergeProjectsMutation.error?.response?.data?.detail || "שגיאה באיחוד"}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowMergeProjects(false)} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
              <button
                onClick={() => {
                  if (!mergePrimaryProject) return;
                  const [type, primaryId] = mergePrimaryProject.split(":");
                  const secondaryIds = keys.filter(k => k !== mergePrimaryProject).map(k => k.split(":")[1]);
                  mergeProjectsMutation.mutate({ type, primaryId, secondaryIds });
                }}
                disabled={!mergePrimaryProject || mergeProjectsMutation.isPending}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
                {mergeProjectsMutation.isPending ? "מאחד..." : "איחוד"}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* ── Bulk delete maintenance confirm ─────────────────────────────────── */}
    {showBulkDeleteMaint && (() => {
      const keys = [...selectedMaintItems];
      const maintIds  = keys.filter(k => k.startsWith("maintenance:")).map(k => k.split(":")[1]);
      const ticketIds = keys.filter(k => k.startsWith("ticket:")).map(k => k.split(":")[1]);
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
            <h3 className="text-base font-semibold text-gray-800">אישור מחיקה</h3>
            <p className="text-sm text-gray-600">האם למחוק {selectedMaintItems.size} פריטים? פעולה זו אינה הפיכה.</p>
            {bulkDeleteMaintMutation.isError && (
              <p className="text-xs text-red-500">{bulkDeleteMaintMutation.error?.response?.data?.detail || "שגיאה במחיקה"}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowBulkDeleteMaint(false)} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
              <button onClick={() => bulkDeleteMaintMutation.mutate({ maintIds, ticketIds })}
                disabled={bulkDeleteMaintMutation.isPending}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
                {bulkDeleteMaintMutation.isPending ? "מוחק..." : "מחק"}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* ── Merge maintenance dialog ─────────────────────────────────────────── */}
    {showMergeMaint && (() => {
      const keys = [...selectedMaintItems];
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl border p-6 w-[26rem] space-y-4" dir="rtl">
            <h3 className="text-base font-semibold">איחוד פריטים</h3>
            <p className="text-sm text-gray-600">בחר את הפריט הראשי שאליו יאוחדו שאר הפריטים:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {keys.map(k => {
                const [type, id] = k.split(":");
                const row = [...maintenanceItems, ...maintenanceTickets].find(r => r.id === id);
                return (
                  <label key={k} className="flex items-center gap-2.5 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                    <input type="radio" name="merge-primary-maint" value={k}
                      checked={mergePrimaryMaint === k}
                      onChange={() => setMergePrimaryMaint(k)}
                      className="accent-green-600" />
                    <span className="text-sm flex-1">{row?.title || id}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${type === "maintenance" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                      {type === "maintenance" ? "תחזוקה" : "קריאה"}
                    </span>
                  </label>
                );
              })}
            </div>
            {mergeMaintMutation.isError && (
              <p className="text-xs text-red-500">{mergeMaintMutation.error?.response?.data?.detail || "שגיאה באיחוד"}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowMergeMaint(false)} className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
              <button
                onClick={() => {
                  if (!mergePrimaryMaint) return;
                  const [type, primaryId] = mergePrimaryMaint.split(":");
                  const secondaryIds = keys.filter(k => k !== mergePrimaryMaint).map(k => k.split(":")[1]);
                  mergeMaintMutation.mutate({ type, primaryId, secondaryIds });
                }}
                disabled={!mergePrimaryMaint || mergeMaintMutation.isPending}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
                {mergeMaintMutation.isPending ? "מאחד..." : "איחוד"}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    </>
  );
}
